import React, { useState, useEffect } from 'react';
import { useTheme } from '../../utils/ThemeSwitcher';
import { 
  X, 
  Send, 
  Bug, 
  Lightbulb, 
  Heart, 
  HelpCircle, 
  MoreHorizontal,
  Star,
  CheckCircle,
  AlertCircle,
  Loader,
  Mail,
  MapPin
} from 'lucide-react';

/**
 * FeedbackModal Component
 * Modal form for collecting user feedback during beta testing.
 * Auto-captures context like current view and user info.
 */

// Country list for dropdown
const COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Argentina", "Australia", "Austria",
  "Bangladesh", "Belgium", "Botswana", "Brazil", "Burkina Faso", "Burundi",
  "Cambodia", "Cameroon", "Canada", "Chad", "Chile", "China", "Colombia", "Congo",
  "Côte d'Ivoire", "Czech Republic", "Denmark", "DRC", "Ecuador", "Egypt",
  "Ethiopia", "Finland", "France", "Germany", "Ghana", "Greece", "Guatemala",
  "Haiti", "Honduras", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel",
  "Italy", "Jamaica", "Japan", "Jordan", "Kenya", "Lesotho", "Liberia", "Libya",
  "Madagascar", "Malawi", "Malaysia", "Mali", "Mexico", "Morocco", "Mozambique",
  "Myanmar", "Namibia", "Nepal", "Netherlands", "New Zealand", "Nicaragua",
  "Niger", "Nigeria", "Norway", "Pakistan", "Panama", "Papua New Guinea", "Peru",
  "Philippines", "Poland", "Portugal", "Rwanda", "Saudi Arabia", "Senegal",
  "Sierra Leone", "Singapore", "Somalia", "South Africa", "South Korea",
  "South Sudan", "Spain", "Sri Lanka", "Sudan", "Sweden", "Switzerland",
  "Tanzania", "Thailand", "Togo", "Tunisia", "Turkey", "Uganda", "Ukraine",
  "United Arab Emirates", "United Kingdom", "United States", "Uruguay",
  "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe", "Other"
];

// Feedback type options
const FEEDBACK_TYPES = [
  { id: 'bug', label: 'Bug Report', icon: Bug, color: '#EF4444', description: 'Something is broken or not working correctly' },
  { id: 'suggestion', label: 'Suggestion', icon: Lightbulb, color: '#D97706', description: 'Ideas for new features or improvements' },
  { id: 'praise', label: 'Praise', icon: Heart, color: '#10B981', description: 'Something you love about DataQuad' },
  { id: 'question', label: 'Question', icon: HelpCircle, color: '#2563EB', description: 'Need help or clarification' },
  { id: 'other', label: 'Other', icon: MoreHorizontal, color: '#8B5CF6', description: 'General feedback' },
];

// Map view names to friendly labels
const VIEW_LABELS = {
  'projects': 'Projects',
  'primary-survey': 'Primary Survey Loader',
  'xlsform': 'XLSForm Loader',
  'backcheck-dataset': 'Back-Check Data Loader',
  'data-source': 'Data Loader',
  'datepreview': 'Data Preview',
  'data-summary': 'Data Summary',
  'correlation-matrix': 'Correlation Matrix',
  'collector-dashboard': 'Collector Dashboard',
  'quality-checks': 'Configure Detectors',
  'configure-manual': 'Configure Detectors',
  'dashboard': 'Detective Dashboard',
  'configure-back-checks': 'Configure Back-Checks',
  'backcheckdashboard': 'Back-Check Dashboard',
  'ml-dashboard': 'AI Assessment',
  'cleaning-studio': 'Cleaning Studio',
  'cleaned-review': 'Comparison Dashboard',
  'data-export': 'Data Export',
  'generate-report': 'Generate Report',
  'quick-scan-progress': 'Quick Scan',
  'quick-scan-summary': 'Quick Scan Summary',
};

const FeedbackModal = ({ 
  isOpen, 
  onClose, 
  userInfo,
  currentView,
  currentProjectId 
}) => {
  const { isDarkMode } = useTheme();
  
  // Form state
  const [feedbackType, setFeedbackType] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [country, setCountry] = useState('');
  const [rating, setRating] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  
  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFeedbackType('');
      setSubject('');
      setDescription('');
      setRating(0);
      setSubmitStatus(null);
      setErrorMessage('');
    }
  }, [isOpen]);

  const colors = {
    overlay: isDarkMode ? 'rgba(0, 0, 0, 0.55)' : 'rgba(0, 0, 0, 0.4)',
    bg: isDarkMode ? '#111114' : '#FFFFFF',
    subtleBg: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
    border: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)',
    borderStrong: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.12)',
    text: isDarkMode ? '#f0ede4' : '#1a1a1a',
    textSecondary: isDarkMode ? '#a8a49a' : '#555550',
    textMuted: isDarkMode ? '#8a867d' : '#6B7280',
    ink: isDarkMode ? '#f0ede4' : '#1a1a1a',
    paper: isDarkMode ? '#0D0E0E' : '#F8F5EE',
    success: '#10B981',
    warning: '#D97706',
    error: '#EF4444',
    star: '#D97706',
    info: '#2563EB',
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!feedbackType) {
      setErrorMessage('Please select a feedback type');
      return;
    }
    if (!description.trim()) {
      setErrorMessage('Please provide a description');
      return;
    }
    
    setIsSubmitting(true);
    setErrorMessage('');
    
    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'https://api.dataquadapp.com';
      
      const response = await fetch(`${API_BASE}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'user_email': userInfo?.email || '',
        },
        body: JSON.stringify({
          feedback_type: feedbackType,
          subject: subject.trim() || null,
          description: description.trim(),
          country: country || null,
          rating: rating > 0 ? rating : null,
          current_view: currentView || null,
          current_project_id: currentProjectId || null,
          browser_info: navigator.userAgent,
          screen_resolution: `${window.screen.width}x${window.screen.height}`,
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to submit feedback');
      }
      
      setSubmitStatus('success');
      
      // Auto-close after success
      setTimeout(() => {
        onClose();
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmitStatus('error');
      setErrorMessage(error.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Get friendly view label
  const viewLabel = VIEW_LABELS[currentView] || currentView || 'Unknown page';

  // Success state
  if (submitStatus === 'success') {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: colors.overlay,
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px',
        fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
      }}>
        <div style={{
          backgroundColor: colors.bg,
          borderRadius: '8px',
          border: `0.5px solid ${colors.borderStrong}`,
          padding: '36px 32px',
          textAlign: 'center',
          maxWidth: '380px',
          width: '100%',
          animation: 'fadeIn 0.3s ease-out',
        }}>
          <div style={{
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            backgroundColor: isDarkMode ? 'rgba(16, 185, 129, 0.12)' : 'rgba(16, 185, 129, 0.08)',
            border: `0.5px solid rgba(16, 185, 129, 0.3)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 18px',
          }}>
            <CheckCircle size={22} color={colors.success} />
          </div>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '500',
            color: colors.text,
            marginBottom: '6px',
            margin: '0 0 6px 0',
            letterSpacing: '-0.02em',
          }}>
            Thank you
          </h3>
          <p style={{
            fontSize: '12px',
            color: colors.textSecondary,
            lineHeight: '1.5',
            margin: 0,
            opacity: 0.85,
          }}>
            Your feedback helps us make DataQuad better for everyone.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: colors.overlay,
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
        fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div style={{
        backgroundColor: colors.bg,
        borderRadius: '8px',
        border: `0.5px solid ${colors.borderStrong}`,
        width: '100%',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        animation: 'slideUp 0.3s ease-out',
      }}>
        {/* Header */}
        <div style={{
          padding: '18px 22px',
          borderBottom: `0.5px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '12px',
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: '500',
              color: colors.text,
              margin: 0,
              letterSpacing: '-0.02em',
              lineHeight: 1.25,
            }}>
              Send feedback
            </h2>
            <p style={{
              fontSize: '12px',
              color: colors.textSecondary,
              margin: '3px 0 0',
              opacity: 0.8,
            }}>
              Help us improve DataQuad
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              padding: '6px',
              cursor: 'pointer',
              color: colors.textMuted,
              borderRadius: '8px',
              transition: 'all 0.15s ease',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.subtleBg;
              e.currentTarget.style.color = colors.text;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = colors.textMuted;
            }}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} style={{
          padding: '20px 22px',
          overflow: 'auto',
          flex: 1,
        }}>
          {/* Feedback Type Selection */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '10px',
              fontWeight: '500',
              color: colors.textSecondary,
              marginBottom: '10px',
              textTransform: 'lowercase',
              letterSpacing: '0.06em',
            }}>
              what type of feedback is this? <span style={{ color: colors.error, letterSpacing: 0 }}>*</span>
            </label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gap: '6px',
            }}>
              {FEEDBACK_TYPES.map((type) => {
                const Icon = type.icon;
                const isSelected = feedbackType === type.id;
                return (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setFeedbackType(type.id)}
                    title={type.description}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '10px 6px',
                      borderRadius: '6px',
                      border: isSelected
                        ? `0.5px solid ${type.color}`
                        : `0.5px solid ${colors.border}`,
                      backgroundColor: isSelected
                        ? (isDarkMode ? `${type.color}14` : `${type.color}0F`)
                        : 'transparent',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      fontFamily: 'inherit',
                    }}
                  >
                    <Icon size={14} color={isSelected ? type.color : colors.textMuted} strokeWidth={1.75} />
                    <span style={{
                      fontSize: '10.5px',
                      fontWeight: '500',
                      color: isSelected ? type.color : colors.textSecondary,
                      letterSpacing: '-0.005em',
                      lineHeight: 1.2,
                    }}>
                      {type.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Subject (Optional) */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '10px',
              fontWeight: '500',
              color: colors.textSecondary,
              marginBottom: '6px',
              textTransform: 'lowercase',
              letterSpacing: '0.06em',
            }}>
              subject <span style={{ color: colors.textMuted, fontWeight: '400' }}>(optional)</span>
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Brief summary of your feedback"
              maxLength={255}
              style={{
                width: '100%',
                padding: '9px 12px',
                borderRadius: '8px',
                border: `0.5px solid ${colors.border}`,
                backgroundColor: colors.subtleBg,
                color: colors.text,
                fontSize: '12.5px',
                outline: 'none',
                transition: 'border-color 0.15s ease',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = colors.borderStrong}
              onBlur={(e) => e.currentTarget.style.borderColor = colors.border}
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '10px',
              fontWeight: '500',
              color: colors.textSecondary,
              marginBottom: '6px',
              textTransform: 'lowercase',
              letterSpacing: '0.06em',
            }}>
              description <span style={{ color: colors.error, letterSpacing: 0 }}>*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Please describe your feedback in detail. The more specific, the better we can help!"
              rows={4}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: `0.5px solid ${colors.border}`,
                backgroundColor: colors.subtleBg,
                color: colors.text,
                fontSize: '12.5px',
                outline: 'none',
                resize: 'vertical',
                minHeight: '96px',
                fontFamily: 'inherit',
                lineHeight: 1.5,
                transition: 'border-color 0.15s ease',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = colors.borderStrong}
              onBlur={(e) => e.currentTarget.style.borderColor = colors.border}
            />
          </div>

          {/* Country Dropdown */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '10px',
              fontWeight: '500',
              color: colors.textSecondary,
              marginBottom: '6px',
              textTransform: 'lowercase',
              letterSpacing: '0.06em',
            }}>
              country <span style={{ color: colors.textMuted, fontWeight: '400' }}>(optional)</span>
            </label>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              style={{
                width: '100%',
                padding: '9px 12px',
                borderRadius: '8px',
                border: `0.5px solid ${colors.border}`,
                backgroundColor: colors.subtleBg,
                color: country ? colors.text : colors.textMuted,
                fontSize: '12.5px',
                outline: 'none',
                cursor: 'pointer',
                transition: 'border-color 0.15s ease',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                backgroundImage: `url("data:image/svg+xml;charset=US-ASCII,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='none' stroke='${isDarkMode ? '%23a8a49a' : '%236B7280'}' stroke-width='1' stroke-linecap='round' stroke-linejoin='round' d='M1 1l4 4 4-4'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
                backgroundSize: '10px 6px',
                paddingRight: '30px',
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = colors.borderStrong}
              onBlur={(e) => e.currentTarget.style.borderColor = colors.border}
            >
              <option value="">Select your country</option>
              {COUNTRIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* Star Rating */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '10px',
              fontWeight: '500',
              color: colors.textSecondary,
              marginBottom: '8px',
              textTransform: 'lowercase',
              letterSpacing: '0.06em',
            }}>
              overall experience <span style={{ color: colors.textMuted, fontWeight: '400' }}>(optional)</span>
            </label>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              {[1, 2, 3, 4, 5].map((star) => {
                const active = hoveredStar >= star || rating >= star;
                return (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoveredStar(star)}
                    onMouseLeave={() => setHoveredStar(0)}
                    aria-label={`${star} star${star > 1 ? 's' : ''}`}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: '4px',
                      cursor: 'pointer',
                      transition: 'transform 0.15s ease',
                      transform: active ? 'scale(1.08)' : 'scale(1)',
                      display: 'inline-flex',
                    }}
                  >
                    <Star
                      size={20}
                      fill={active ? colors.star : 'transparent'}
                      color={active ? colors.star : colors.textMuted}
                      strokeWidth={1.5}
                    />
                  </button>
                );
              })}
              {rating > 0 && (
                <span style={{
                  marginLeft: '8px',
                  fontSize: '11px',
                  color: colors.textSecondary,
                  alignSelf: 'center',
                  letterSpacing: '-0.005em',
                }}>
                  {rating === 1 && 'Poor'}
                  {rating === 2 && 'Fair'}
                  {rating === 3 && 'Good'}
                  {rating === 4 && 'Very good'}
                  {rating === 5 && 'Excellent'}
                </span>
              )}
            </div>
          </div>

          {/* Context Info (auto-captured, shown as reference) */}
          <div style={{
            padding: '10px 12px',
            backgroundColor: colors.subtleBg,
            border: `0.5px solid ${colors.border}`,
            borderRadius: '6px',
            marginBottom: '16px',
          }}>
            <div style={{
              fontSize: '9.5px',
              color: colors.textMuted,
              marginBottom: '6px',
              textTransform: 'lowercase',
              letterSpacing: '0.08em',
              fontWeight: 500,
            }}>
              auto-captured context
            </div>
            <div style={{
              fontSize: '11.5px',
              color: colors.textSecondary,
              display: 'flex',
              flexWrap: 'wrap',
              gap: '14px',
              alignItems: 'center',
            }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                <Mail size={11} style={{ opacity: 0.7, flexShrink: 0 }} />
                {userInfo?.email || 'Unknown'}
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                <MapPin size={11} style={{ opacity: 0.7, flexShrink: 0 }} />
                {viewLabel}
              </span>
            </div>
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 12px',
              backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.06)',
              border: `0.5px solid rgba(239, 68, 68, 0.3)`,
              borderRadius: '6px',
              marginBottom: '14px',
            }}>
              <AlertCircle size={13} color={colors.error} />
              <span style={{ fontSize: '12px', color: colors.error, letterSpacing: '-0.005em' }}>
                {errorMessage}
              </span>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              width: '100%',
              padding: '11px 20px',
              borderRadius: '100px',
              border: 'none',
              backgroundColor: isSubmitting
                ? (isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)')
                : colors.ink,
              color: isSubmitting
                ? colors.textMuted
                : colors.paper,
              fontSize: '12.5px',
              fontWeight: '500',
              letterSpacing: '-0.005em',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'all 0.15s ease',
              fontFamily: 'inherit',
              opacity: isSubmitting ? 0.7 : 1,
            }}
          >
            {isSubmitting ? (
              <>
                <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} />
                Submitting...
              </>
            ) : (
              <>
                <Send size={13} />
                Submit feedback
              </>
            )}
          </button>
        </form>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { 
            opacity: 0; 
            transform: translateY(20px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default FeedbackModal;